// Disable the wishlist Add-to-Cart button for items that are out of stock
// in the current store's region. Stock is signalled through the item `tags`:
//   - "OOS-NZ" -> out of stock for zempire.co.nz
//   - "OOS-AU" -> out of stock for zempire.com.au
// Data source: window.FishAPI.app.wishlists ({ WL1: [...], WL2: [...], ... }).
// Flip to false to silence the console output in production.
const WISHLIST_OOS_DEBUG = true;
const wishlistLog = (...args) => {
    if (WISHLIST_OOS_DEBUG) console.log('[wishlist-oos]', ...args);
};

document.addEventListener('DOMContentLoaded', () => {
    // The wishlist data lives on FishAPI at load time, but the drawer markup
    // (the wishlist-item-card elements) is injected asynchronously by the app.
    // So run once now, then keep watching the DOM and re-run whenever new cards
    // are added (e.g. the first time the drawer renders or re-renders).
    initWishlistOOS();
    watchForWishlistCards();
    watchForPdpWishlistAdd();
});

function watchForPdpWishlistAdd() {
    // Adding a product to the wishlist from the PDP (the <wishlist-button-pdp>
    // heart) does not go through the same render path the observer catches, so
    // the new item's ATC button keeps its default state until a page refresh.
    // Listen for that click and re-run the OOS check once the drawer has caught
    // up. The add is asynchronous: both the new card and its FishAPI data settle
    // at an unpredictable moment, so poll until the clicked item is actually
    // present in the data before applying (rather than guessing a fixed delay).
    document.addEventListener('click', (event) => {
        const pdpButton = event.target.closest('wishlist-button-pdp');
        if (!pdpButton) return;

        const variantId = pdpButton.dataset.variantId;
        wishlistLog('PDP wishlist button clicked', { variantId });
        pollUntilItemReady(variantId);
    });
}

function pollUntilItemReady(variantId, attempt = 0) {
    const MAX_ATTEMPTS = 20; // ~6s at 300ms - enough for the async add to land
    const INTERVAL_MS = 300;

    // The item is "ready" once it exists in the FishAPI wishlists data; only
    // then can initWishlistOOS match its card and toggle the button correctly.
    const inData = Object.values(window.FishAPI?.app?.wishlists || {}).some(
        (items) => Array.isArray(items) && items.some((item) => String(item?.id) === String(variantId))
    );

    if (inData) {
        wishlistLog(`PDP item ${variantId} ready in data (attempt ${attempt}) - applying`);
        initWishlistOOS();
        return;
    }

    if (attempt >= MAX_ATTEMPTS) {
        wishlistLog(`PDP item ${variantId} not found in data after ${attempt} attempts - giving up`);
        return;
    }

    setTimeout(() => pollUntilItemReady(variantId, attempt + 1), INTERVAL_MS);
}

function watchForWishlistCards() {
    let scheduled = false;
    const observer = new MutationObserver((mutations) => {
        // Only react when an actual card (or a container holding one) is added,
        // to avoid re-running on unrelated DOM changes - including our own
        // `disabled` toggles, which are attribute changes we are not watching.
        const cardAdded = mutations.some((m) =>
            [...m.addedNodes].some(
                (node) =>
                    node.nodeType === 1 &&
                    (node.matches?.('wishlist-item-card') || node.querySelector?.('wishlist-item-card'))
            )
        );
        if (!cardAdded || scheduled) return;

        // Debounce: a render can add many cards in one tick.
        scheduled = true;
        requestAnimationFrame(() => {
            scheduled = false;
            wishlistLog('cards detected via observer - re-running');
            initWishlistOOS();
        });
    });

    observer.observe(document.body, { childList: true, subtree: true });
}

function initWishlistOOS() {
    // 1. Pull the wishlists object from the FishAPI global.
    const wishLists = window.FishAPI?.app?.wishlists;
    if (!wishLists) {
        wishlistLog('FishAPI.app.wishlists not found - aborting', { FishAPI: window.FishAPI });
        return;
    }
    wishlistLog('wishlists loaded', wishLists);

    // Button labels come from the app's translations map so they stay localised.
    const translations = window.FishAPI?.app?.translations || {};

    // Flatten every wishlist array into a single id -> item lookup so that a
    // card can be matched regardless of which wishlist it belongs to.
    const itemsById = new Map();
    Object.values(wishLists).forEach((items) => {
        if (!Array.isArray(items)) return;
        items.forEach((item) => {
            if (item && item.id != null) {
                itemsById.set(String(item.id), item);
            }
        });
    });
    wishlistLog(`built lookup of ${itemsById.size} item(s)`, [...itemsById.keys()]);

    const hostname = window.location.hostname;
    const isNZ = hostname.includes('zempire.co.nz');
    const isAU = hostname.includes('zempire.com.au');
    wishlistLog('domain', { hostname, isNZ, isAU });

    // 2. Walk through every rendered wishlist card.
    const cards = document.querySelectorAll('wishlist-item-card[data-id]');
    wishlistLog(`found ${cards.length} card(s)`);

    cards.forEach((card) => {
        const id = card.dataset.id;
        const item = itemsById.get(id);
        if (!item) {
            wishlistLog(`card ${id} skipped - no matching item in wishlists`);
            return;
        }

        // The ATC button is the <button> with the fish-wishlist-button class.
        const atcButton = card.querySelector('button.fish-wishlist-button');
        if (!atcButton) {
            wishlistLog(`card ${id} skipped - no ATC button found`);
            return;
        }

        // Tags live in different places depending on how the item entered the
        // wishlist: items loaded on page render expose `item.tags`, while items
        // just added live from the PDP expose them under `item.product.tags`.
        const tags = Array.isArray(item.tags)
            ? item.tags
            : Array.isArray(item.product?.tags)
              ? item.product.tags
              : [];
        const hasTag = (name) => tags.some((tag) => String(tag).trim().toUpperCase() === name);

        const shouldDisable = (isNZ && hasTag('OOS-NZ')) || (isAU && hasTag('OOS-AU'));

        atcButton.disabled = shouldDisable;

        // Swap the button label using the app's translations: "Sold Out" when
        // disabled, otherwise the "Add to Cart" label. The label lives in the
        // inner <span>.
        const label = atcButton.querySelector('span');
        if (label) {
            label.textContent = shouldDisable ? translations['sold-out-button'] : translations['atc-button'];
        }

        wishlistLog(`card ${id}`, {
            title: item.name || item.productTitle,
            tags,
            shouldDisable,
            disabled: atcButton.disabled,
            label: label ? label.textContent : null,
        });
    });
}
